import { PopoverProps } from '@radix-ui/react-popover'
import { useState, type ReactNode, type ComponentProps, memo, useCallback } from 'react'

import { Button, Dialog, DialogTrigger, DialogContent, DialogTitle, SimpleTooltip } from '@fp/ui-kit'

interface ConfirmActionButtonProps extends PopoverProps {
  button: ReactNode
  title: string
  message: ReactNode
  onConfirm: VoidFunction | (() => Promise<void>)
  tooltipText?: ReactNode
  contentClassName?: string
  size?: ComponentProps<typeof DialogContent>['size']
}

export const ConfirmActionButton = memo(
  ({ button, message, onConfirm, title, tooltipText, contentClassName, size }: ConfirmActionButtonProps) => {
    const [open, setOpen] = useState(false)
    const [loading, setLoading] = useState(false)

    const handleConfirm = useCallback(async () => {
      try {
        setLoading(true)
        await onConfirm()
      } finally {
        setLoading(false)
        setOpen(false)
      }
    }, [onConfirm])

    const trigger = <DialogTrigger asChild>{button}</DialogTrigger>

    return (
      <Dialog open={open} onOpenChange={setOpen}>
        {tooltipText ? <SimpleTooltip content={tooltipText}>{trigger}</SimpleTooltip> : trigger}

        <DialogContent className={contentClassName} size={size}>
          <DialogTitle className='text-center'>{title}</DialogTitle>
          <div className='flex flex-col items-center gap-6'>
            <div>{message}</div>
            <div className='flex justify-center gap-2'>
              <Button variant='primary' disabled={loading} onClick={handleConfirm} isLoading={loading}>
                Confirm
              </Button>

              <Button variant='outline' onClick={() => setOpen(false)}>
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    )
  },
)

ConfirmActionButton.displayName = 'ConfirmActionButton'
